import { ArrowLeft, BellRing, Building2, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';

type SettingsDrilldownProps = {
  open: boolean;
  onBack: () => void;
  onNavigate: () => void;
  isCollapsed: boolean;
  isActive: (route?: string) => boolean;
};

const settingsItems = [
  { label: 'Notifications', route: '/settings/notifications', Icon: BellRing },
  { label: 'Permissions', route: '/settings/permissions', Icon: ShieldCheck },
  { label: 'Departments', route: '/settings/departments', Icon: Building2 },
];

export default function SettingsDrilldown({
  open,
  onBack,
  onNavigate,
  isCollapsed,
  isActive,
}: SettingsDrilldownProps) {
  return (
    <div
      aria-hidden={!open}
      className={`absolute inset-0 z-20 max-w-full overflow-x-hidden overflow-y-auto bg-main transition-[transform,opacity] duration-300 ease-out ${
        open ? 'translate-x-0 opacity-100' : 'pointer-events-none -translate-x-[1.6rem] opacity-0'
      }`}
    >
      <SidebarMenu
        className={`flex flex-col px-[1.85rem] ${
          isCollapsed ? 'mt-[2rem] items-center gap-[2.8rem] !overflow-visible' : 'gap-[1.2rem]'
        }`}
      >
        <SidebarMenuItem className={isCollapsed ? undefined : 'mb-[1.6rem]'}>
          <SidebarMenuButton
            type="button"
            onClick={onBack}
            aria-label="Back to main menu"
            tabIndex={open ? 0 : -1}
            tooltip={isCollapsed ? 'Back to main menu' : undefined}
            className={`text-[1.5rem] family-[montserrat] md:px-[1.2rem] ${
              isCollapsed
                ? '!h-[4.4rem] !w-[4.4rem] !rounded-[1rem] !p-0'
                : 'rounded-[1.2rem]'
            }`}
          >
            <span
              className={`group/item flex h-[5rem] w-full items-center ${
                isCollapsed ? 'justify-center' : 'gap-[1rem]'
              }`}
            >
              <span className={`flex shrink-0 items-center justify-center ${isCollapsed ? 'h-[1.8rem] w-[1.8rem]' : 'h-[2rem] w-[2rem]'}`}>
                <ArrowLeft className="!h-full !w-full text-second" />
              </span>
              <span
                className={`${
                  isCollapsed ? 'hidden' : ''
                } font-semibold text-[1.5rem] leading-none text-main`}
              >
                Settings
              </span>
            </span>
          </SidebarMenuButton>
        </SidebarMenuItem>

        <div className="w-full">
          <p
            className={`${
              isCollapsed ? 'hidden' : 'ml-[1rem]'
            } font-normal text-[1.3rem] leading-[120%] tracking-[0] text-disable`}
          >
            Account
          </p>
          <div className={`${isCollapsed ? 'mt-0' : 'mt-[1.6rem]'}`}>
            {settingsItems.map(({ label, route, Icon }) => (
              <SidebarMenuItem key={route}>
                <SidebarMenuButton
                  asChild
                  tooltip={isCollapsed ? label : undefined}
                  className={`text-[1.5rem] family-[montserrat] md:px-[1.2rem] ${
                    isCollapsed
                      ? '!h-[4.4rem] !w-[4.4rem] !rounded-[1rem] !p-0'
                      : 'rounded-[1.2rem]'
                  }`}
                >
                  <Link
                    to={route}
                    tabIndex={open ? 0 : -1}
                    onClick={onNavigate}
                    className={`group/item flex h-[5rem] w-full ${
                      isActive(route) ? '!bg-interactive' : 'dark:bg-transparent'
                    } ${isCollapsed ? 'items-center justify-center' : 'gap-[1rem]'}`}
                  >
                    <div className={`icon flex shrink-0 items-center justify-center ${isCollapsed ? 'h-[1.8rem] w-[1.8rem]' : 'h-[2rem] w-[2rem]'}`}>
                      <Icon
                        className={`!h-full !w-full ${isActive(route) ? 'text-main' : 'text-second'} transition-all ease duration-300`}
                      />
                    </div>
                    <span
                      className={`${
                        isCollapsed ? 'hidden' : ''
                      } font-semibold text-[1.5rem] leading-none tracking-[0] align-middle ${
                        isActive(route) ? 'text-main' : 'text-second'
                      }`}
                    >
                      {label}
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </div>
        </div>
      </SidebarMenu>
    </div>
  );
}
